type BackendUrlConfig = { REACT_APP_BACKEND_URL: string };

function assertBackendUrlConfig(value: unknown): asserts value is BackendUrlConfig {
  if (typeof value !== 'object' || value === null || typeof (value as { REACT_APP_BACKEND_URL?: unknown }).REACT_APP_BACKEND_URL !== 'string') {
    throw new Error('config.json is malformed: missing or non-string REACT_APP_BACKEND_URL');
  }
}

/**
 * Memoised runtime config.json loader. The file is static per deployment, but
 * several callers need it at startup (backend URL + telemetry settings), so we
 * fetch it ONCE and share the result instead of hitting the network per caller.
 * Returns `null` on any load failure; failures are NOT cached, so a later call
 * can retry. Never throws.
 */
let runtimeConfigPromise: Promise<Record<string, unknown> | null> | null = null;

export const getRuntimeConfig = (): Promise<Record<string, unknown> | null> => {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = (async () => {
      const response = await fetch('/config.json', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Failed to load config.json');
      }
      return (await response.json()) as Record<string, unknown>;
    })().catch((error) => {
      console.error('Error loading config.json:', error);
      runtimeConfigPromise = null; // don't cache failures — allow a later retry
      return null;
    });
  }
  return runtimeConfigPromise;
};

export const getBackendUrl = async (): Promise<string> => {
  try {
    const config = await getRuntimeConfig();
    assertBackendUrlConfig(config);
    return config.REACT_APP_BACKEND_URL;
  } catch (error) {
    console.error('Error loading backend URL from config.json:', error);
    // Fallback to the dynamic URL generation if config.json is not
    // available, fails to load, or doesn't contain a usable URL.
    const frontendDomain = window.location.hostname;
    return `https://api.${frontendDomain}`;
  }
};

export type TelemetryConfig = {
  enabled: boolean;
  /** logs ingest endpoint, derived from the backend URL */
  endpoint: string;
  /**
   * OTLP/HTTP traces endpoint — the backend's /telemetry/traces proxy by
   * default (parity with logs). OTEL_TRACES_URL overrides it for local dev only.
   */
  tracesUrl: string;
  captureVerboseConsole: boolean;
  /** deployment environment (e.g. production/staging) for per-env filtering. */
  environment: string;
  /**
   * App-level ingest token (RUM). Sent as the Authorization fallback whenever
   * there is no usable user JWT, so pre-login browser errors reach the backend
   * (mapped to authenticated=false + default tenant). Empty when not configured.
   *
   * THIS VALUE IS PUBLIC. It arrives in /config.json, which the frontend
   * container serves to anyone who asks (`curl https://<host>/config.json`), and
   * it is readable in devtools by every visitor. Being stored in a Kubernetes
   * Secret buys rotation hygiene, NOT confidentiality. It identifies the deployed
   * frontend, it does not authenticate anybody: the backend maps it to
   * authenticated=false and the default tenant and nothing else, so it can never
   * be used to reach another tenant's data. Never reuse it as a general-purpose
   * service credential, and never widen what it is accepted for beyond
   * /telemetry/* ingest.
   */
  ingestToken: string;
  /**
   * Minimum level shipped to the backend ingest endpoint ('debug' | 'info' |
   * 'warn' | 'error'). Defaults to 'info' so redux-logger debug state dumps stay
   * in the browser console instead of reaching Loki (issue #1558).
   */
  minLevel: 'debug' | 'info' | 'warn' | 'error';
};

/**
 * Reads telemetry settings from the same runtime config.json (no rebuild per
 * environment). OTEL_ENABLED toggles browser logging; the ingest endpoint is
 * derived from the resolved backend URL. Defaults to disabled and never throws.
 */
export const getTelemetryConfig = async (): Promise<TelemetryConfig> => {
  const backendUrl = await getBackendUrl();
  let enabled = false;
  let captureVerboseConsole = false;
  let tracesUrl = '';
  let environment = 'development';
  let ingestToken = '';
  let minLevel: TelemetryConfig['minLevel'] = 'info';
  try {
    // Reuse the memoised config.json (already loaded by getBackendUrl above) so
    // telemetry adds no extra network round-trip at startup.
    const config = (await getRuntimeConfig()) as {
      OTEL_ENABLED?: boolean | string;
      OTEL_CAPTURE_VERBOSE_CONSOLE?: boolean | string;
      OTEL_TRACES_URL?: string;
      OTEL_ENVIRONMENT?: string;
      OTEL_INGEST_TOKEN?: string;
      OTEL_MIN_LEVEL?: string;
    } | null;
    if (config) {
      enabled = config.OTEL_ENABLED === true || config.OTEL_ENABLED === 'true';
      captureVerboseConsole = config.OTEL_CAPTURE_VERBOSE_CONSOLE === true || config.OTEL_CAPTURE_VERBOSE_CONSOLE === 'true';
      if (typeof config.OTEL_TRACES_URL === 'string') {
        tracesUrl = config.OTEL_TRACES_URL;
      }
      if (typeof config.OTEL_ENVIRONMENT === 'string' && config.OTEL_ENVIRONMENT) {
        environment = config.OTEL_ENVIRONMENT;
      }
      if (typeof config.OTEL_INGEST_TOKEN === 'string') {
        ingestToken = config.OTEL_INGEST_TOKEN;
      }
      // Unrecognised values fall through to the 'info' default rather than
      // accidentally widening what is shipped.
      //
      // Trimmed because config.json is hand-edited and Helm-templated, where a
      // trailing newline or space is easy to introduce and invisible to read:
      // `"error "` would otherwise silently ship MORE than the operator asked
      // for. A non-empty value we still do not recognise is warned about, so the
      // fail-safe is distinguishable from the key simply being absent.
      const lvl = String(config.OTEL_MIN_LEVEL ?? '')
        .trim()
        .toLowerCase();
      if (lvl === 'debug' || lvl === 'info' || lvl === 'warn' || lvl === 'error') {
        minLevel = lvl;
      } else if (lvl) {
        console.warn(`[telemetry] Unrecognised OTEL_MIN_LEVEL '${lvl}' in config.json — falling back to '${minLevel}'.`);
      }
    }
  } catch {
    // leave telemetry disabled on any config error
  }
  return {
    enabled,
    endpoint: `${backendUrl.replace(/\/$/, '')}/telemetry/logs`,
    // Send browser traces to the BACKEND (which proxies them to the Collector),
    // so the browser never contacts the Collector directly — parity with logs.
    // OTEL_TRACES_URL can still override (e.g. to post OTLP straight to a gateway).
    tracesUrl: tracesUrl || `${backendUrl.replace(/\/$/, '')}/telemetry/traces`,
    captureVerboseConsole,
    environment,
    ingestToken,
    minLevel,
  };
};
